import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import {
	ChartCardConfig,
	ChartType,
	ComparisonConfig,
} from '../../../models/chart-config.model';
import { CardError } from '../../../models/card-model';
import { CardRuntimeRefreshStateService } from '../../../services/card-runtime-refresh-state.service';
import { DataService } from '../../../services/data.service';
import { resolveCardTextField } from '../../../utils/card-chrome.util';
import { openCardErrorDetailsDialog } from '../../../utils/card-error-display.util';
import {
	chartCardHasPreviewQueryFields,
	hasRenderableChartPayload,
	isSettledEmptyChartPayload,
} from '../../editors/jepto-chart-editor/services/chart-preview-data.util';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { JeptoLineChartComponent } from '../../charts/jepto-line-chart/jepto-line-chart.component';
import { JeptoAreaChartComponent } from '../../charts/jepto-area-chart/jepto-area-chart.component';
import { JeptoBarChartComponent } from '../../charts/jepto-bar-chart/jepto-bar-chart.component';
import { JeptoColumnChartComponent } from '../../charts/jepto-column-chart/jepto-column-chart.component';
import { JeptoDonutChartComponent } from '../../charts/jepto-donut-chart/jepto-donut-chart.component';
import { JeptoScatterChartComponent } from '../../charts/jepto-scatter-chart/jepto-scatter-chart.component';
import { JeptoNumberChartComponent } from '../../charts/jepto-number-chart/jepto-number-chart.component';
import { JeptoUiProgressBarIndeterminateComponent } from '../../../../ui-lib/buttons-indicators/jepto-ui-progress-bar-indeterminate/jepto-ui-progress-bar-indeterminate.component';
import { JeptoCardQueryStatusComponent } from '../jepto-card-query-status/jepto-card-query-status.component';
import { CardRuntimeDataRefreshBroker } from '../../../services/card-runtime-data-refresh.broker';
import { log } from '@jepto/shared/utils/logger';

/**
 * Chart data structure with optional comparison data
 */
export interface ChartData {
	data?: {
		series?: any[];
		donutSeries?: Array<{ name: string; value: number }>;
		value?: number;
		previousValue?: number;
		changePercent?: number;
		changeDirection?: 'up' | 'down' | 'flat';
		sparklineData?: [string, number][];
		format?: {
			prefix?: string;
			suffix?: string;
			precision?: number;
		};
		/** Number-chart comparison (% change) — nested under `data` (backend shape). */
		comparisonData?: {
			value: number;
			unit?: string;
		};
		total?: number;
		centerLabel?: {
			title: string;
			subtitle?: string;
		};
	};
}

/**
 * Chart Card Component
 *
 * Displays charts (line, area, bar, column, donut, scatter) based on configuration.
 *
 * Supports two modes:
 * - **Standalone**: Provide [config] and [data] directly
 * - **Connected**: Provide [cardId] to fetch from services
 */
@Component({
	selector: 'jepto-chart-card',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class.jepto-chart-card--edit-mode]': 'isEditMode()',
	},
	imports: [
		MatIconModule,
		MatMenuModule,
		JeptoLineChartComponent,
		JeptoAreaChartComponent,
		JeptoBarChartComponent,
		JeptoColumnChartComponent,
		JeptoDonutChartComponent,
		JeptoScatterChartComponent,
		JeptoNumberChartComponent,
		JeptoUiProgressBarIndeterminateComponent,
		JeptoCardQueryStatusComponent,
	],
	templateUrl: './jepto-chart-card.component.html',
	styleUrls: ['./jepto-chart-card.component.scss'],
})
export class JeptoChartCardComponent extends JeptoBaseCardComponent {
	// ═══════════════════════════════════════════════════════════════════════════
	// CARD-SPECIFIC INPUTS
	// ═══════════════════════════════════════════════════════════════════════════

	/** Chart configuration (standalone mode) */
	config = input<ChartCardConfig>();

	/** Chart data (standalone mode) */
	data = input<ChartData>();

	/** Comparison configuration for period-over-period analysis */
	comparison = input<ComparisonConfig>();

	/** Test ID for component targeting in tests (defaults to cardId for uniqueness) */
	testId = input<string>();

	/** Report theme palette id for chart color resolution (editor preview / report canvas). */
	reportThemePaletteId = input<string | undefined>(undefined);

	/**
	 * When true (e.g. line-chart editor preview), render the chart shell without waiting
	 * for {@link effectiveData} and show line/area/… even when series are empty.
	 */
	allowEmptyChart = input(false);

	/**
	 * When true (e.g. editor preview date range change), show the top indeterminate progress bar
	 * while new data is being fetched. Does not hide existing chart content — overlays a loader
	 * on top of the current chart while new data arrives for the selected date range.
	 */
	isPreviewDataLoading = input<boolean>(false);

	/** Computed testId that defaults to cardId when available */
	protected effectiveTestId = computed(
		() =>
			this.testId() ??
			this.effectiveConfig()?.cardId ??
			this.cardId() ??
			'chart-card'
	);

	// ═══════════════════════════════════════════════════════════════════════════
	// SERVICES
	// ═══════════════════════════════════════════════════════════════════════════

	private dataService = inject(DataService, { optional: true });
	private readonly cardRuntimeRefreshState = inject(
		CardRuntimeRefreshStateService,
		{ optional: true }
	);
	private readonly refreshBroker = inject(CardRuntimeDataRefreshBroker, {
		optional: true,
	});
	private dialog = inject(MatDialog);

	// ═══════════════════════════════════════════════════════════════════════════
	// INTERNAL STATE
	// ═══════════════════════════════════════════════════════════════════════════

	// ═══════════════════════════════════════════════════════════════════════════
	// COMPUTED SIGNALS
	// ═══════════════════════════════════════════════════════════════════════════

	/**
	 * Effective config: merge CardState (saved editor/API data) with the layout
	 * `config` input. The container always passes a minimal `config` (ids, chartType,
	 * page/view context); if we returned only that, we would ignore `visualization`
	 * and other fields updated on save — so the page preview would stay stale.
	 * Layout fields overlay the service copy (e.g. pageId from current layout).
	 */
	protected effectiveConfig = computed(() => {
		const direct = this.config();
		const fromService = this.baseConfig() as ChartCardConfig | null;

		if (direct && fromService) {
			// Overlay every defined field from `direct` so layout overrides win,
			// but skip undefined values — a blind spread would clobber service-only
			// fields like data, visualization, title, subtitle.
			const overlay: Partial<ChartCardConfig> = {};
			for (const key of Object.keys(
				direct
			) as (keyof ChartCardConfig)[]) {
				const value = direct[key];
				if (value !== undefined) {
					(overlay as any)[key] = value;
				}
			}
			return {
				...fromService,
				...overlay,
				// Prefer persisted chart type from service, fallback to layout
				chartType:
					fromService.chartType ?? direct.chartType ?? ChartType.Line,
			};
		}
		if (direct) return direct;
		return fromService;
	});

	/** Effective data - direct input takes priority over service */
	protected effectiveData = computed(() => {
		const direct = this.data();
		if (direct) return direct;

		// Fallback to service
		const id = this.cardId();
		if (id && this.dataService) {
			return this.dataService.cardDataSignal(id)() as
				| ChartData
				| undefined;
		}
		return undefined;
	});

	/** Chart type from config */
	protected chartType = computed(
		() => this.effectiveConfig()?.chartType ?? 'line'
	);

	/** Prepared config for chart components (removes title/subtitle) */
	protected preparedConfig = computed(() => {
		const cfg = this.effectiveConfig();
		if (!cfg) return null;

		// Clone and remove title/subtitle (displayed in card header, not chart)
		const prepared = { ...cfg };
		delete (prepared as any).title;
		delete (prepared as any).subtitle;
		return prepared;
	});

	/** Hide chart body until config and data exist (initial load). */
	protected isBlockingLoad = computed(() => {
		const cfg = this.effectiveConfig();
		if (!cfg) return true;
		if (this.allowEmptyChart()) return false;
		return !this.effectiveData();
	});

	/** Report canvas refresh (date/filter change) from {@link CardRuntimeRefreshStateService}. */
	private readonly isReportCanvasRefreshing = computed(() => {
		const id = this.cardId() ?? this.effectiveConfig()?.cardId;
		if (!id || !this.cardRuntimeRefreshState) {
			return false;
		}
		return this.cardRuntimeRefreshState.loadingCardIds().has(id);
	});

	/** Top progress bar — preview/canvas refresh, or blocking load (not idle create empty). */
	protected showTopProgressBar = computed(() => {
		if (this.isPreviewDataLoading() || this.isReportCanvasRefreshing()) {
			return true;
		}
		if (this.showCreateChartEmptyState()) {
			return false;
		}
		return this.isBlockingLoad();
	});

	/** Override title from base - use config title if available */
	protected override cardConfigForChrome = computed(() =>
		this.effectiveConfig()
	);

	/** Override subtitle from base - include comparison text */
	protected override effectiveSubtitle = computed(() => {
		const base = resolveCardTextField(
			this.subtitle(),
			this.effectiveConfig()?.subtitle
		);
		const comparisonText = this.comparisonText();
		return comparisonText ? `${base}${comparisonText}` : base;
	});

	/** Override style from base - use config style when [config] is passed (e.g. chart editor) */
	protected override effectiveStyle = computed(
		() =>
			this.style() ??
			this.baseConfig()?.style ??
			this.effectiveConfig()?.style
	);

	/** Comparison text for subtitle */
	protected comparisonText = computed(() => {
		const comp = this.comparison();
		const comparisonData = this.effectiveData()?.data?.comparisonData;

		if (!comp?.enabled || !comparisonData) return '';

		const value = comparisonData.value;
		const unit = comparisonData.unit || '%';
		const prefix = value > 0 ? '+' : '';

		return ` (${prefix}${value}${unit} vs ${comp.period})`;
	});

	/** Editor create flow: instructional empty state when chart has no data yet. */
	protected readonly showCreateChartEmptyState = computed(() => {
		const cfg = this.effectiveConfig();
		return (
			this.allowEmptyChart() &&
			Boolean(cfg?.pageId?.trim()) &&
			Boolean(cfg?.viewId?.trim()) &&
			!chartCardHasPreviewQueryFields(cfg) &&
			!this.hasValidData()
		);
	});

	/** Page preview: query finished with nothing to plot. */
	protected readonly showPageQueryEmptyState = computed(
		() =>
			!this.allowEmptyChart() &&
			!this.hasError() &&
			!this.showCreateChartEmptyState() &&
			isSettledEmptyChartPayload(
				this.effectiveData(),
				this.chartType()
			)
	);

	/** Title for create-flow empty state (varies by chart type). */
	protected readonly createChartEmptyStateTitle = computed(() => {
		const labels: Record<string, string> = {
			line: 'Create Line Chart',
			area: 'Create Area Chart',
			bar: 'Create Bar Chart',
			column: 'Create Column Chart',
			donut: 'Create Donut Chart',
			scatter: 'Create Scatter Chart',
		};
		return labels[this.chartType()] ?? 'Create Chart';
	});

	/** Has valid chart data */
	protected hasValidData = computed(() => {
		const data = this.effectiveData();
		if (!data) {
			return false;
		}
		return hasRenderableChartPayload(data, this.chartType());
	});

	/** Chart type checks */
	protected isLineChart = computed(() => this.chartType() === 'line');
	protected isAreaChart = computed(() => this.chartType() === 'area');
	protected isBarChart = computed(() => this.chartType() === 'bar');
	protected isColumnChart = computed(() => this.chartType() === 'column');
	protected isDonutChart = computed(() => this.chartType() === 'donut');
	protected isScatterChart = computed(() => this.chartType() === 'scatter');
	protected isNumberChart = computed(
		() => this.chartType() === ChartType.Number
	);

	/** Palette ID for charts: from config.palette.selectedPaletteId when set, else card palette input. */
	protected effectivePalette = computed(
		() =>
			this.effectiveConfig()?.palette?.selectedPaletteId ?? this.palette()
	);

	protected override configCardErrorSource = computed(
		() => this.effectiveConfig()?.error
	);

	protected override dataCardErrorSource = computed(
		() =>
			(this.effectiveData() as { error?: CardError } | undefined)?.error
	);

	// ═══════════════════════════════════════════════════════════════════════════
	// CONSTRUCTOR & EFFECTS
	// ═══════════════════════════════════════════════════════════════════════════

	constructor() {
		super();

		// Debug logging effect
		effect(() => {
			const id = this.cardId();
			const cfg = this.effectiveConfig();
			const data = this.effectiveData();

			if (this.isDebugMode() && id) {
				log(`[ChartCard ${id}] Config:`, cfg);
				log(`[ChartCard ${id}] Data:`, data);
			}
		});
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// METHODS
	// ═══════════════════════════════════════════════════════════════════════════

	/** Retry loading data after error or empty query. */
	onRetry(): void {
		const cfg = this.effectiveConfig();
		const id = cfg?.cardId ?? this.cardId();
		this.localCardError.set(null);
		if (id && this.cardService) {
			this.cardService.clearCardError(id);
		}
		if (id && this.refreshBroker?.hasListeners) {
			this.cardRuntimeRefreshState?.beginCardRefresh(id);
			this.refreshBroker.requestRefresh(id);
		}
	}

	/** Open error details modal */
	openErrorDetails(): void {
		const err = this.cardError();
		if (!err) return;

		openCardErrorDetailsDialog(this.dialog, err, {
			cardId: this.effectiveConfig()?.cardId ?? this.cardId(),
			cardTitle: this.effectiveTitle(),
		});
	}
}
